import { useEffect, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useNavigate, useRouter } from '@tanstack/react-router';
import {
  assignUserRole,
  grantUserPermission,
  impersonateUser,
  revokeUserPermission,
  revokeUserRole,
  setUserStatus,
} from '@/api/generated/sdk.gen';
import { userQuery, rolesQuery, permissionsQuery, type ScopeDto, type UserDetail } from '@/lib/users';
import { apartmentsQuery } from '@/lib/apartments';
import { meQuery, usePermissions } from '@/lib/me';
import { ApiError, messageFromError } from '@/lib/api';
import { formatDateTime, formatDateUTC } from '@/lib/format';
import { formatRole } from '@/lib/nav';
import { posthog } from '@/lib/posthog';
import { usePageHeader } from '@/components/PageHeader';
import { useConfirm } from '@/components/DialogProvider';
import { useToast } from '@/components/ToastProvider';
import { Avatar } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { MultiSelectFilter, type FilterOption } from '@/components/ui/multi-select-filter';
import { RadioGroup } from '@/components/ui/radio-group';
import { Select } from '@/components/ui/select';
import { UserStatusBadge } from './users.index';
import type { UserGrant, UserRoleAssignment } from '@/api/generated/types.gen';

export const Route = createFileRoute('/_app/users/$id')({
  loader: async ({ context, params }) => {
    try {
      await context.queryClient.ensureQueryData(userQuery(params.id));
    } catch (err) {
      const status = (err as { status?: number })?.status;
      if (status === 401 || status === 404) return;
      throw err;
    }
  },
  component: UserDetailPage,
});

function UserDetailPage() {
  const { id } = Route.useParams();
  const { data, error, isLoading } = useQuery(userQuery(id));

  // Title lives in the Topbar (navTitle → "Users"); publish the user's display
  // name as the subtitle, falling back to navSubtitle until it loads.
  usePageHeader({ subtitle: data?.display_name });

  const is401 = error instanceof ApiError && error.status === 401;
  const is404 = error instanceof ApiError && error.status === 404;
  const errorMessage = is401
    ? 'Your session has expired.'
    : is404
      ? 'User not found.'
      : error
        ? messageFromError(error)
        : null;

  return (
    <div className="max-w-3xl">
      <nav aria-label="Breadcrumb" className="mb-4 text-sm text-muted">
        <Link to="/users" className="hover:underline">Users</Link>
        <span aria-hidden="true"> / </span>
        <span className="text-foreground">{data?.display_name ?? id.slice(0, 8)}</span>
      </nav>

      {isLoading && <p className="text-sm text-muted">Loading…</p>}

      {errorMessage && (
        <div
          role="alert"
          className="mb-6 rounded-[var(--radius-control)] border border-[var(--ollio-danger)] bg-[var(--ollio-danger-bg)] px-4 py-3 text-sm text-destructive"
        >
          {errorMessage}
          {is401 && (
            <>
              {' '}
              <Link to="/login" className="font-medium underline">
                Sign in again
              </Link>
              .
            </>
          )}
        </div>
      )}

      {data && <UserDetailView user={data} />}
    </div>
  );
}

export function UserDetailView({ user }: { user: UserDetail }) {
  return (
    <>
      <ProfileSection user={user} />
      <RolesSection user={user} />
      <GrantsSection user={user} />
    </>
  );
}

// useUserEditInvalidator — after any mutation on a user, refresh the users
// list; and when the actor edited THEIR OWN account, also refresh ['me'] so
// usePermissions() re-gates the UI (a non-super-admin editing their own
// roles/grants would otherwise keep stale gating from the ['me'] cache).
function useUserEditInvalidator(userId: string) {
  const qc = useQueryClient();
  const { data: me } = useQuery(meQuery);
  return () => {
    qc.invalidateQueries({ queryKey: ['users'] });
    if (me?.id === userId) qc.invalidateQueries({ queryKey: ['me'] });
  };
}

function ProfileSection({ user }: { user: UserDetail }) {
  const { can } = usePermissions();
  const qc = useQueryClient();
  const invalidate = useUserEditInvalidator(user.id);
  const { toast } = useToast();
  const askConfirm = useConfirm();
  const navigate = useNavigate();
  const router = useRouter();
  const { data: me } = useQuery(meQuery);

  const impersonateMut = useMutation({
    mutationFn: async () => {
      await impersonateUser({ throwOnError: true, path: { id: user.id } });
    },
    onSuccess: async () => {
      // Explicit property: the impersonating super prop is registered only
      // after the me refetch — without it the start event lacks the target.
      posthog.capture?.('pms_action', {
        action: 'impersonation_started',
        impersonated_user_id: user.id,
      });
      // Navigate FIRST: clearing while still mounted on this page makes every
      // active query refetch as the target (403 burst). Then purge — every
      // cached dataset was fetched with the ADMIN's permissions and must not
      // leak into the target's view. The router's loader cache (Sidebar
      // identity, nav gating) has to go too, not just React Query.
      await navigate({ to: '/' });
      qc.clear();
      void router.invalidate();
    },
    onError: (err) => {
      toast({ kind: 'error', title: 'Could not impersonate', message: messageFromError(err) });
    },
  });

  // Client-side gate mirrors the server's guard ladder (which re-enforces all
  // of it): permission, active target, not yourself, not a super_admin, and
  // no impersonation already running.
  const canImpersonate =
    can('users.impersonate') &&
    user.status === 'active' &&
    me?.id !== user.id &&
    !me?.impersonation &&
    !user.role_assignments.some((a) => a.role === 'super_admin');

  async function handleImpersonate() {
    const ok = await askConfirm({
      title: 'Impersonate this user?',
      body: `You will see and act as ${user.display_name} for up to 60 minutes. Every action is audited under your name.`,
      confirmLabel: 'Impersonate',
    });
    if (!ok) return;
    impersonateMut.mutate();
  }

  const statusMut = useMutation({
    mutationFn: async (status: 'active' | 'disabled') => {
      const res = await setUserStatus({ throwOnError: true, path: { id: user.id }, body: { status } });
      return res.data;
    },
    onSuccess: (updated) => {
      qc.setQueryData(userQuery(user.id).queryKey, updated);
      invalidate();
      toast({
        kind: 'success',
        title: updated.status === 'active' ? 'User activated' : 'User deactivated',
        message: user.display_name,
      });
      posthog.capture?.('pms_action', { action: 'user_status_changed', status: updated.status });
    },
    onError: (err) => {
      toast({ kind: 'error', title: 'Could not update status', message: messageFromError(err) });
    },
  });

  async function handleToggle() {
    if (user.status === 'active') {
      const ok = await askConfirm({
        title: 'Deactivate user?',
        body: `${user.display_name} will lose access immediately.`,
        destructive: true,
        confirmLabel: 'Deactivate user',
      });
      if (!ok) return;
      statusMut.mutate('disabled');
    } else {
      statusMut.mutate('active');
    }
  }

  return (
    <section className="mb-6 rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-5">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <Avatar name={user.display_name || user.email} size="lg" />
          <div className="min-w-0">
            <h1 className="truncate font-serif text-2xl font-bold text-[var(--text-heading)]">
              {user.display_name}
            </h1>
            <div className="mt-1">
              <UserStatusBadge status={user.status} />
            </div>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {canImpersonate && (
            <Button
              variant="secondary"
              size="sm"
              loading={impersonateMut.isPending}
              disabled={impersonateMut.isPending}
              onClick={handleImpersonate}
            >
              Impersonate
            </Button>
          )}
          {can('users.disable') && (
            <Button
              variant={user.status === 'active' ? 'destructive' : 'secondary'}
              size="sm"
              loading={statusMut.isPending}
              disabled={statusMut.isPending}
              onClick={handleToggle}
            >
              {user.status === 'active' ? 'Deactivate' : 'Activate'}
            </Button>
          )}
        </div>
      </div>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm md:grid-cols-2">
        <Field label="Email" value={user.email} />
        <Field
          label="Last login"
          value={user.last_login_at ? formatDateTime(user.last_login_at) : 'Never'}
        />
        <Field label="Created" value={formatDateTime(user.created_at)} />
      </dl>
    </section>
  );
}

function RolesSection({ user }: { user: UserDetail }) {
  const { can } = usePermissions();
  const canManage = can('users.assign_role');
  const [showAdd, setShowAdd] = useState(false);

  return (
    <section className="mb-6 rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-5">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xs font-semibold uppercase tracking-[0.06em] text-muted">Roles</h2>
        {canManage && (
          <Button variant="secondary" size="sm" onClick={() => setShowAdd(true)}>
            <Plus className="h-4 w-4" />
            Add role
          </Button>
        )}
      </div>

      {user.role_assignments.length === 0 ? (
        <p className="text-sm text-muted">No roles assigned.</p>
      ) : (
        <ul className="divide-y divide-[var(--border-soft)] overflow-hidden rounded-[var(--radius-control)] border border-[var(--border-hairline)]">
          {user.role_assignments.map((a, i) => (
            <RoleRow key={`${a.role}-${i}`} userId={user.id} assignment={a} canManage={canManage} />
          ))}
        </ul>
      )}

      {showAdd && <AddRoleDialog userId={user.id} onClose={() => setShowAdd(false)} />}
    </section>
  );
}

function RoleRow({
  userId,
  assignment,
  canManage,
}: {
  userId: string;
  assignment: UserRoleAssignment;
  canManage: boolean;
}) {
  const invalidate = useUserEditInvalidator(userId);
  const { toast } = useToast();

  const revokeMut = useMutation({
    mutationFn: async () =>
      revokeUserRole({ throwOnError: true, path: { id: userId, role: assignment.role } }),
    onSuccess: () => {
      invalidate();
      toast({ kind: 'success', title: 'Role removed', message: formatRole(assignment.role) });
      posthog.capture?.('pms_action', { action: 'user_role_revoked' });
    },
    onError: (err) => {
      toast({ kind: 'error', title: 'Could not remove role', message: messageFromError(err) });
    },
  });

  return (
    <li className="flex flex-col gap-2 bg-card px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-2">
        <Badge variant="navy">{formatRole(assignment.role)}</Badge>
        <span className="text-sm text-muted">{describeScope(assignment.scope)}</span>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-xs text-muted">
          Granted {formatDateTime(assignment.granted_at)}
          {assignment.granted_by && <> by <span className="font-mono">{assignment.granted_by.slice(0, 8)}</span></>}
        </span>
        {canManage && (
          <Button
            variant="ghost"
            size="sm"
            loading={revokeMut.isPending}
            disabled={revokeMut.isPending}
            onClick={() => revokeMut.mutate()}
            aria-label={`Remove role: ${formatRole(assignment.role)}`}
          >
            Remove
          </Button>
        )}
      </div>
    </li>
  );
}

function GrantsSection({ user }: { user: UserDetail }) {
  const { can } = usePermissions();
  const canManage = can('users.assign_role');
  const [showAdd, setShowAdd] = useState(false);

  return (
    <section className="mb-6 rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-5">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xs font-semibold uppercase tracking-[0.06em] text-muted">Direct grants</h2>
        {canManage && (
          <Button variant="secondary" size="sm" onClick={() => setShowAdd(true)}>
            <Plus className="h-4 w-4" />
            Add grant
          </Button>
        )}
      </div>

      {user.grants.length === 0 ? (
        <p className="text-sm text-muted">No direct grants.</p>
      ) : (
        <ul className="divide-y divide-[var(--border-soft)] overflow-hidden rounded-[var(--radius-control)] border border-[var(--border-hairline)]">
          {user.grants.map((g, i) => (
            <GrantRow key={`${g.action}-${i}`} userId={user.id} grant={g} canManage={canManage} />
          ))}
        </ul>
      )}

      {showAdd && <AddGrantDialog userId={user.id} onClose={() => setShowAdd(false)} />}
    </section>
  );
}

function GrantRow({ userId, grant, canManage }: { userId: string; grant: UserGrant; canManage: boolean }) {
  const invalidate = useUserEditInvalidator(userId);
  const { toast } = useToast();

  const revokeMut = useMutation({
    mutationFn: async () =>
      revokeUserPermission({ throwOnError: true, path: { id: userId, action: grant.action } }),
    onSuccess: () => {
      invalidate();
      toast({ kind: 'success', title: 'Grant removed', message: grant.action });
      posthog.capture?.('pms_action', { action: 'user_permission_revoked' });
    },
    onError: (err) => {
      toast({ kind: 'error', title: 'Could not remove grant', message: messageFromError(err) });
    },
  });

  return (
    <li className="flex flex-col gap-2 bg-card px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="gold">
          <span className="font-mono">{grant.action}</span>
        </Badge>
        <span className="text-sm text-muted">{describeScope(grant.scope)}</span>
        {grant.expires_at && (
          <span className="text-xs text-muted">Expires {formatDateUTC(grant.expires_at)}</span>
        )}
      </div>
      <div className="flex items-center gap-3">
        <span className="text-xs text-muted">
          Granted {formatDateTime(grant.granted_at)}
          {grant.granted_by && <> by <span className="font-mono">{grant.granted_by.slice(0, 8)}</span></>}
        </span>
        {canManage && (
          <Button
            variant="ghost"
            size="sm"
            loading={revokeMut.isPending}
            disabled={revokeMut.isPending}
            onClick={() => revokeMut.mutate()}
            aria-label={`Remove grant: ${grant.action}`}
          >
            Remove
          </Button>
        )}
      </div>
    </li>
  );
}

// ScopePicker — shared "All apartments" vs "Specific apartments" control for
// the Add role / Add grant dialogs. The apartment multiselect reuses
// apartmentsQuery (the same list the rest of the app queries).
function ScopePicker({
  allApartments,
  onAllApartmentsChange,
  apartmentOptions,
  apartmentsLoading,
  selected,
  onSelectedChange,
}: {
  allApartments: boolean;
  onAllApartmentsChange: (v: boolean) => void;
  apartmentOptions: FilterOption[];
  apartmentsLoading: boolean;
  selected: Set<string>;
  onSelectedChange: (s: Set<string>) => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <span className="text-sm font-semibold text-[var(--text-heading)]">Apartments</span>
      <RadioGroup
        row
        options={[
          { value: 'all', label: 'All apartments' },
          { value: 'specific', label: 'Specific apartments' },
        ]}
        value={allApartments ? 'all' : 'specific'}
        onChange={(v) => onAllApartmentsChange(v === 'all')}
      />
      {!allApartments &&
        (apartmentsLoading ? (
          <p className="text-sm text-muted">Loading apartments…</p>
        ) : (
          <MultiSelectFilter
            label="Apartments"
            options={apartmentOptions}
            selected={selected}
            onChange={onSelectedChange}
          />
        ))}
    </div>
  );
}

function AddRoleDialog({ userId, onClose }: { userId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const invalidate = useUserEditInvalidator(userId);
  const { toast } = useToast();
  const { data: roles, isLoading: rolesLoading } = useQuery(rolesQuery);
  const { data: apartments, isLoading: apartmentsLoading } = useQuery(apartmentsQuery);
  const [role, setRole] = useState('');
  const [allApartments, setAllApartments] = useState(true);
  const [selectedApartments, setSelectedApartments] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!role && roles && roles.length > 0) setRole(roles[0]?.code ?? '');
  }, [roles, role]);

  const roleOptions = (roles ?? []).map((r) => ({ value: r.code, label: formatRole(r.code) }));
  const apartmentOptions: FilterOption[] = (apartments ?? []).map((a) => ({
    value: a.id,
    label: `${a.code} — ${a.title}`,
  }));
  const isSuperAdmin = role === 'super_admin';

  const mut = useMutation({
    mutationFn: async () => {
      if (!role) throw new Error('Choose a role.');
      const scope: ScopeDto =
        isSuperAdmin || allApartments
          ? { all_apartments: true, apartments: [] }
          : { all_apartments: false, apartments: Array.from(selectedApartments) };
      if (!scope.all_apartments && scope.apartments.length === 0) {
        throw new Error('Choose at least one apartment, or select "All apartments".');
      }
      const res = await assignUserRole({ throwOnError: true, path: { id: userId }, body: { role, scope } });
      return res.data;
    },
    onSuccess: (updated) => {
      qc.setQueryData(userQuery(userId).queryKey, updated);
      invalidate();
      toast({ kind: 'success', title: 'Role added', message: formatRole(role) });
      posthog.capture?.('pms_action', { action: 'user_role_assigned' });
      onClose();
    },
    onError: (err) => setError(messageFromError(err)),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title="Add role"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mut.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={mut.isPending}
            disabled={mut.isPending}
            onClick={() => {
              setError(null);
              mut.mutate();
            }}
          >
            Assign role
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Select
          label="Role"
          options={roleOptions}
          placeholder={rolesLoading ? 'Loading roles…' : roleOptions.length === 0 ? 'No roles available' : undefined}
          value={role}
          onChange={(e) => setRole(e.target.value)}
          disabled={rolesLoading || roleOptions.length === 0}
        />
        {isSuperAdmin ? (
          <p className="text-sm text-muted">Super admin always has access to all apartments.</p>
        ) : (
          <ScopePicker
            allApartments={allApartments}
            onAllApartmentsChange={setAllApartments}
            apartmentOptions={apartmentOptions}
            apartmentsLoading={apartmentsLoading}
            selected={selectedApartments}
            onSelectedChange={setSelectedApartments}
          />
        )}
        {error && (
          <p role="alert" aria-live="assertive" className="text-sm text-destructive">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}

function AddGrantDialog({ userId, onClose }: { userId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const invalidate = useUserEditInvalidator(userId);
  const { toast } = useToast();
  const { data: actions, isLoading: actionsLoading } = useQuery(permissionsQuery);
  const { data: apartments, isLoading: apartmentsLoading } = useQuery(apartmentsQuery);
  const [action, setAction] = useState('');
  const [allApartments, setAllApartments] = useState(true);
  const [selectedApartments, setSelectedApartments] = useState<Set<string>>(new Set());
  const [expiresAt, setExpiresAt] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!action && actions && actions.length > 0) setAction(actions[0] ?? '');
  }, [actions, action]);

  const actionOptions = (actions ?? []).map((a) => ({ value: a, label: a }));
  const apartmentOptions: FilterOption[] = (apartments ?? []).map((a) => ({
    value: a.id,
    label: `${a.code} — ${a.title}`,
  }));

  const mut = useMutation({
    mutationFn: async () => {
      if (!action) throw new Error('Choose an action.');
      const scope: ScopeDto = allApartments
        ? { all_apartments: true, apartments: [] }
        : { all_apartments: false, apartments: Array.from(selectedApartments) };
      if (!scope.all_apartments && scope.apartments.length === 0) {
        throw new Error('Choose at least one apartment, or select "All apartments".');
      }
      const trimmedExpiry = expiresAt.trim();
      const res = await grantUserPermission({
        throwOnError: true,
        path: { id: userId },
        body: {
          action,
          scope,
          expires_at: trimmedExpiry ? new Date(`${trimmedExpiry}T00:00:00Z`).toISOString() : undefined,
        },
      });
      return res.data;
    },
    onSuccess: (updated) => {
      qc.setQueryData(userQuery(userId).queryKey, updated);
      invalidate();
      toast({ kind: 'success', title: 'Grant added', message: action });
      posthog.capture?.('pms_action', { action: 'user_permission_granted' });
      onClose();
    },
    onError: (err) => setError(messageFromError(err)),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title="Add direct grant"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mut.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={mut.isPending}
            disabled={mut.isPending}
            onClick={() => {
              setError(null);
              mut.mutate();
            }}
          >
            Grant permission
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Select
          label="Action"
          options={actionOptions}
          placeholder={
            actionsLoading ? 'Loading actions…' : actionOptions.length === 0 ? 'No actions available' : undefined
          }
          value={action}
          onChange={(e) => setAction(e.target.value)}
          disabled={actionsLoading || actionOptions.length === 0}
        />
        <ScopePicker
          allApartments={allApartments}
          onAllApartmentsChange={setAllApartments}
          apartmentOptions={apartmentOptions}
          apartmentsLoading={apartmentsLoading}
          selected={selectedApartments}
          onSelectedChange={setSelectedApartments}
        />
        <Input
          label="Expires"
          type="date"
          value={expiresAt}
          onChange={(e) => setExpiresAt(e.target.value)}
          hint="Leave blank for no expiry."
        />
        {error && (
          <p role="alert" aria-live="assertive" className="text-sm text-destructive">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}

function describeScope(scope: { all_apartments: boolean; apartments: string[] }): string {
  if (scope.all_apartments) return 'All apartments';
  const n = scope.apartments.length;
  if (n === 0) return 'No apartments';
  if (n <= 3) return scope.apartments.map((a) => a.slice(0, 8)).join(', ');
  return `${n} apartments`;
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-[0.06em] text-muted">{label}</dt>
      <dd className="mt-0.5">{value}</dd>
    </div>
  );
}
